/**
 * Leasing API — typed wrappers around `/v1/leasing/*`.
 *
 * Mirrors the OpenAPI shapes in `api/src/schemas/leasing.ts`. Re-declared
 * locally so the module compiles when the gitignored
 * `frontend/src/types/api.ts` hasn't been refreshed.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { getAuthHeaders } from '@/lib/auth';

export type LeasingKind = 'nabidka' | 'smlouva';

function pathFor(kind: LeasingKind, id?: number | string, suffix = ''): string {
  const base = kind === 'nabidka' ? '/v1/leasing/nabidky' : '/v1/leasing/smlouvy';
  if (id == null) return `${base}${suffix}`;
  return `${base}/${id}${suffix}`;
}

export interface LeasingSmlouva {
  id_smlouvy: number;
  predcasne_ukonceno: string | null;
  datum_do: string | null;
  typ_vozu: string;
  cena_celkem: string;
  splatka: number;
  id_zakaznika: number | null;
  jmeno: string | null;
  jmeno_uzivatele: string | null;
}

export interface LeasingNabidka {
  id_smlouvy: number;
  id_uzivatele: number;
  typ_vozu: string;
  cena_celkem: string;
  splatka: number;
  id_zakaznika: number | null;
  jmeno: string | null;
  jmeno_uzivatele: string | null;
}

export interface LeasingSearchResult {
  id_smlouvy: number;
  vin: string;
  registracni_znacka: string;
  id_zakaznika: number | null;
  typ_vozu: string;
  dph: string;
  id_firmy: number;
  predcasne_ukonceno: string | null;
  datum_do: string | null;
  nazev_firmy: string | null;
  jmeno_zakaznika: string | null;
}

interface SmluvyListResponse {
  smlouvy: LeasingSmlouva[];
}

interface NabidkyListResponse {
  nabidky: LeasingNabidka[];
}

interface SearchResponse {
  results: LeasingSearchResult[];
}

export interface SmluvyListFilters {
  hledanejmeno?: string;
  cislo_smlouvy?: string;
  limit?: string;
}

export interface NabidkyListFilters {
  hledanejmeno?: string;
  cislo_nabidky?: string;
  limit?: string;
}

export interface SearchFilters {
  vin?: string;
  registracni_znacka?: string;
}

function buildQuery(filters: Record<string, string | undefined | null>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (typeof value === 'string' && value.trim()) {
      params.set(key, value.trim());
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useSmluvyQuery(filters: SmluvyListFilters, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['leasing', 'smlouvy', filters],
    enabled: options?.enabled ?? true,
    queryFn: ({ signal }) =>
      api.get<SmluvyListResponse>(
        `/v1/leasing/smlouvy${buildQuery({ ...filters })}`,
        { signal },
      ),
    placeholderData: (previous) => previous,
  });
}

export function useNabidkyQuery(filters: NabidkyListFilters, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['leasing', 'nabidky', filters],
    enabled: options?.enabled ?? true,
    queryFn: ({ signal }) =>
      api.get<NabidkyListResponse>(
        `/v1/leasing/nabidky${buildQuery({ ...filters })}`,
        { signal },
      ),
    placeholderData: (previous) => previous,
  });
}

export interface LeasingFirma {
  id_firmy: number;
  Nazev: string;
}

export interface LeasingFirmyResponse {
  firmy: LeasingFirma[];
  currentCompanyId: number;
}

/**
 * Companies the current user may copy a leasing offer into (`GET
 * /v1/leasing/firmy`). Drives the duplicate/copy menu: the entry matching
 * `currentCompanyId` is an in-place duplicate, the rest are cross-company
 * copies.
 */
export function useLeasingFirmyQuery(enabled = true) {
  return useQuery({
    queryKey: ['leasing', 'firmy'],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: ({ signal }) =>
      api.get<LeasingFirmyResponse>('/v1/leasing/firmy', { signal }),
  });
}

/**
 * Duplicate a leasing offer (legacy "Duplikovat / Kopírovat nabídku"). Server
 * deep-copies the nabídka + its parametry/náklady rows and returns the new id.
 * Optional `id_cilove_firmy` copies into another dealership (cross-company
 * copy, resets the customer); omit it for an in-place duplicate.
 */
export function useCopyNabidkaMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, id_cilove_firmy }: { id: number; id_cilove_firmy?: number }) =>
      api.post<{ id: number }>(
        `/v1/leasing/nabidky/${id}/copy`,
        id_cilove_firmy ? { id_cilove_firmy } : {},
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leasing', 'nabidky'] }),
  });
}

/**
 * Copy a leasing contract into another company. The
 * contract is bound to `id_zakaznika` — a customer that already exists in (or
 * was just copied into) `id_firmy`. Server deep-copies the smlouva + protokol
 * + parametry + náklady rows and returns the new id.
 */
export function useCopySmlouvaMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      id_zakaznika,
      id_firmy,
    }: {
      id: number;
      id_zakaznika: number;
      id_firmy: number;
    }) =>
      api.post<{ id: number }>(`/v1/leasing/smlouvy/${id}/copy`, {
        id_zakaznika,
        id_firmy,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leasing', 'smlouvy'] }),
  });
}

// ==================== Smlouva detail ====================

/**
 * `smlouva` is `operak_smlouva` left-joined with the customer columns. The
 * API returns it as `Record<string, unknown>` so types stay loose; helpers in
 * `helpers.ts` know how to read the fields used by the detail UI.
 */
export interface SmlouvaDetailResponse {
  smlouva: Record<string, unknown>;
  protokol: Record<string, unknown> | null;
  parametry: Record<string, unknown> | null;
  company: Record<string, unknown> | null;
  seller: Record<string, unknown> | null;
  vatRate: number;
}

export function useSmlouvaDetailQuery(id: number | null) {
  return useQuery({
    queryKey: ['leasing', 'smlouva', id],
    enabled: id != null && Number.isFinite(id),
    // Backs the leasing wizard / financing editors — always refetch on mount so
    // edit pages open against fresh server state (the detail/summary surfaces
    // don't keep this cache warm after a save).
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      api.get<SmlouvaDetailResponse>(`/v1/leasing/smlouvy/${id}`, { signal }),
  });
}

export interface LoanInsurance {
  vynos_uv: number;
  spl_uv: number;
  uver_spol: string;
  cislo_uveru: string;
  datum_pocatek: string;
  datum_konec: string;
  pojistovatel: string;
  cislo_pojistky: string;
  pojisteni: number;
  havarijni_pojisteni: number;
}

interface LoanInsuranceResponse {
  loanInsurance: LoanInsurance | null;
}

export function useLoanInsuranceQuery(id: number | null) {
  return useQuery({
    queryKey: ['leasing', 'smlouva', id, 'loan-insurance'],
    enabled: id != null && Number.isFinite(id),
    // Seeds the financing editor — always refetch on mount for fresh edit state.
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      api.get<LoanInsuranceResponse>(
        `/v1/leasing/smlouvy/${id}/loan-insurance`,
        { signal },
      ),
  });
}

export interface CostItem {
  id: number;
  id_smlouvy: number;
  druh_nakladu: string;
  castka: number;
  typ: string;
  datum: string;
}

export interface CostsResponse {
  costs: CostItem[];
  revenues: CostItem[];
  dph: string;
}

export function useCostsQuery(id: number | null) {
  return useQuery({
    queryKey: ['leasing', 'smlouva', id, 'costs'],
    enabled: id != null && Number.isFinite(id),
    // Seeds the náklady/výnosy editor — always refetch on mount for fresh state.
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      api.get<CostsResponse>(`/v1/leasing/smlouvy/${id}/costs`, { signal }),
  });
}

// ==================== Loan/Insurance + Costs mutations ====================

export function useUpsertLoanInsuranceMutation(id: number, exists: boolean) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LoanInsurance) => {
      const path = `/v1/leasing/smlouvy/${id}/loan-insurance`;
      return exists ? api.put<{ status: 'ok' }>(path, body) : api.post<{ status: 'ok' }>(path, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leasing', 'smlouva', id, 'loan-insurance'] });
      qc.invalidateQueries({ queryKey: ['leasing', 'smlouva', id] });
    },
  });
}

export interface CostBatchItem {
  druh_nakladu: string;
  castka: number;
  datum: string;
}

export function useReplaceCostsMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { costs: CostBatchItem[]; revenues: CostBatchItem[] }) =>
      api.put<{ status: 'ok' }>(`/v1/leasing/smlouvy/${id}/costs`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leasing', 'smlouva', id, 'costs'] });
    },
  });
}

// ==================== Wizard mutations ====================

export interface VehicleStepBody {
  typ_vozu: string;
  provedeni: string;
  verze: string;
  barva: string;
  motor: string;
  prevodovka: string;
  dph: string;
  zakladni_cena: number;
  sleva_zakladni_cena: number;
}

export interface ParametersStepBody {
  delka: number;
  km_rok: number;
  kauce_pocatek: number;
  kauce_konec: number;
  kauce_rozpustit: number;
  havarijni: number;
  povinne: number;
  servis: number;
  pneu: number;
  radio: number;
  dalnice: number;
  skla: number;
  nahradni_auto: number;
  gps: number;
  zustatkova_cena: number;
  letni_pneu: string;
  zimni_pneu: string;
  hav_spolu: string;
  pov_limit: string;
  km_do: string;
  km_pres: string;
  tolerance: string;
}

export interface CalculationData {
  cena_celkem: number;
  delka: number;
  dph: string;
  kauce_pocatek: number;
  kauce_konec: number;
  zustatkova_cena: number;
  havarijni: number;
  povinne: number;
  servis: number;
  pneu: number;
  radio: number;
  dalnice: number;
  skla: number;
  nahradni_auto: number;
  splatka: number;
  bilance: number;
  urok_doc: number;
  vyr_fa: number;
  fleet: number;
  bonusy: number;
  hav_nak: number;
  pov_nak: number;
  serv_nak: number;
  pneu_nak: number;
  radio_nak: number;
  dal_nak: number;
  skla_nak: number;
  nahrad_a_nak: number;
  vynos_uv: number;
  pocet_splatek: number;
  spl_uv: number;
}

export function useCreateLeasingMutation(kind: LeasingKind) {
  return useMutation({
    mutationFn: (id_zakaznika: number) =>
      api.post<{ id: number }>(pathFor(kind), { id_zakaznika }),
  });
}

export function useAssignCustomerMutation(kind: LeasingKind, id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id_zakaznika: number) =>
      api.put<{ status: 'ok' }>(pathFor(kind, id, '/customer'), { id_zakaznika }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leasing'] }),
  });
}

export function useUpdateVehicleMutation(kind: LeasingKind, id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: VehicleStepBody) =>
      api.put<{ status: 'ok' }>(pathFor(kind, id, '/vehicle'), body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leasing'] }),
  });
}

export function useUpdateParametersMutation(kind: LeasingKind, id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ParametersStepBody) =>
      api.put<{ status: 'ok' }>(pathFor(kind, id, '/parameters'), body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leasing'] }),
  });
}

export function useCalculationQuery(
  kind: LeasingKind,
  id: number | null,
  jinyurok?: string,
) {
  return useQuery({
    queryKey: ['leasing', 'calculation', kind, id, jinyurok ?? null],
    enabled: id != null && Number.isFinite(id),
    // Seeds the kalkulace wizard step — always refetch on mount for fresh state.
    refetchOnMount: 'always',
    queryFn: ({ signal }) => {
      const qs = jinyurok ? `?jinyurok=${encodeURIComponent(jinyurok)}` : '';
      return api.get<{ calculation: CalculationData }>(
        pathFor(kind, id!, `/calculation${qs}`),
        { signal },
      );
    },
  });
}

export function useSaveCalculationMutation(kind: LeasingKind, id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { splatka: number; urok_mira: number }) =>
      api.put<{ status: 'ok' }>(pathFor(kind, id, '/calculation'), body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leasing'] }),
  });
}

// ==================== Setup costs (Zadání nákladů kalkulace) ====================

/**
 * `operak_*_naklady` acquisition-cost inputs of the calculation. Amounts are yearly, bez DPH, stored as varchar.
 * Field caps mirror `LeasingSetupCostsBodySchema` in `api/src/schemas/leasing.ts`.
 */
export interface SetupCostsBody {
  vyr_fa: string;
  fleet: string;
  bonusy: string;
  hav_nak: string;
  pov_nak: string;
  serv_nak: string;
  pneu_nak: string;
  radio_nak: string;
  dal_nak: string;
  skla_nak: string;
  nahrad_a_nak: string;
  vynos_uv: string;
  pocet_splatek: number;
  spl_uv: string;
}

export interface SetupCostsResponse {
  costs: Record<string, unknown> | null;
  contract: Record<string, unknown> | null;
}

export function useSetupCostsQuery(kind: LeasingKind, id: number | null, enabled = true) {
  return useQuery({
    queryKey: ['leasing', 'setup-costs', kind, id],
    enabled: enabled && id != null && Number.isFinite(id),
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      api.get<SetupCostsResponse>(pathFor(kind, id!, '/setup-costs'), { signal }),
  });
}

export function useUpdateSetupCostsMutation(kind: LeasingKind, id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SetupCostsBody) =>
      api.put<{ status: 'ok' }>(pathFor(kind, id, '/setup-costs'), body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leasing'] }),
  });
}

// ==================== Convert offer → contract ====================

export function useConvertNabidkaToSmlouvaMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ id: number }>(`/v1/leasing/nabidky/${id}/convert-to-contract`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leasing'] }),
  });
}

// ==================== Nabídka detail (for wizard prefills) ====================

export interface NabidkaDetailResponse {
  nabidka: Record<string, unknown>;
  parametry: Record<string, unknown> | null;
  naklady: Record<string, unknown> | null;
  zakaznik: Record<string, unknown> | null;
  company: Record<string, unknown> | null;
  seller: Record<string, unknown> | null;
  vatRate: number;
}

export function useNabidkaDetailQuery(id: number | null) {
  return useQuery({
    queryKey: ['leasing', 'nabidka', id],
    enabled: id != null && Number.isFinite(id),
    // Backs the leasing offer wizard prefills — always refetch on mount so the
    // edit flow opens against fresh server state.
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      api.get<NabidkaDetailResponse>(`/v1/leasing/nabidky/${id}`, { signal }),
  });
}

export function useLeasingDetailQuery(kind: LeasingKind, id: number | null) {
  const smlouva = useSmlouvaDetailQuery(kind === 'smlouva' ? id : null);
  const nabidka = useNabidkaDetailQuery(kind === 'nabidka' ? id : null);
  return kind === 'smlouva' ? smlouva : nabidka;
}

// ==================== Handover protocol (Phase 11.6.6) ====================

/**
 * Body shape for `POST/PUT /v1/leasing/smlouvy/{id}/handover-protocol`. Mirrors
 * `LeasingHandoverProtocolBodySchema` in `api/src/schemas/leasing.ts`. Field
 * widths (varchar caps) are enforced at the DB layer — keep client-side
 * `maxLength` props aligned to avoid 500s on overflow.
 */
export interface LeasingHandoverProtocolBody {
  datum: string;
  technicky_prukaz: string;
  pocet_klicu: string;
  kod_barvy: string;
  vin: string;
  registracni_znacka: string;
  cislo_objednavky: string;
  servis_mesice: string;
  servis_km: string;
  odhad_servisu: string;
  datum_od?: string;
  datum_do?: string;
}

export function useCreateLeasingHandoverProtocolMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LeasingHandoverProtocolBody) =>
      api.post<{ status: 'ok' }>(
        `/v1/leasing/smlouvy/${id}/handover-protocol`,
        body,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leasing', 'smlouva', id] }),
  });
}

export function useUpdateLeasingHandoverProtocolMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LeasingHandoverProtocolBody) =>
      api.put<{ status: 'ok' }>(
        `/v1/leasing/smlouvy/${id}/handover-protocol`,
        body,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leasing', 'smlouva', id] }),
  });
}

// ==================== Přehledy (reports — Phase 6.6) ====================

export type PrehledReport = 'splatky' | 'smlouvy-ukonceni' | 'uvery' | 'uvery-ukonceni';

export interface PrehledSplatka {
  id_smlouvy: number;
  vin: string;
  splatka: number;
  id_zakaznika: number;
  dph: string;
  datum_od: string | null;
  datum_do: string | null;
  predcasne_ukonceno: string | null;
  jmeno: string | null;
}

export interface PrehledSmlouvaUkonceni {
  id_smlouvy: number;
  vin: string;
  id_zakaznika: number;
  datum_od: string | null;
  datum_do: string | null;
  predcasne_ukonceno: string | null;
  jmeno: string | null;
}

export interface PrehledUver {
  id_smlouvy: number;
  spl_uv: number;
  uver_spol: string;
  cislo_uveru: string;
  datum_pocatek: string;
  id_zakaznika: number;
  smlouva_datum_konec: string | null;
  jmeno: string | null;
}

export interface PrehledUverUkonceni {
  cislo_uveru: string;
  uver_spol: string;
  datum_konec: string;
}

interface SplatkyResponse {
  splatky: PrehledSplatka[];
  /** Company VAT percentage (e.g. 21) — used to gross up `dph === 'Ne'` rows. */
  vatRate: number;
}

/**
 * The four month-scoped report endpoints. `mesic` is `YYYY-MM`; the backend treats
 * an empty/absent month as "all". Each hook stays disabled until a month is set
 * so first paint doesn't fire four queries.
 */
export function useSplatkyQuery(mesic: string, enabled = true) {
  return useQuery({
    queryKey: ['leasing', 'prehled', 'splatky', mesic],
    enabled,
    queryFn: ({ signal }) =>
      api.get<SplatkyResponse>(
        `/v1/leasing/prehled/splatky${buildQuery({ mesic })}`,
        { signal },
      ),
    placeholderData: (previous) => previous,
  });
}

export function useSmlouvyUkonceniQuery(mesic: string, enabled = true) {
  return useQuery({
    queryKey: ['leasing', 'prehled', 'smlouvy-ukonceni', mesic],
    enabled,
    queryFn: ({ signal }) =>
      api.get<{ smlouvy: PrehledSmlouvaUkonceni[] }>(
        `/v1/leasing/prehled/smlouvy-ukonceni${buildQuery({ mesic })}`,
        { signal },
      ),
    placeholderData: (previous) => previous,
  });
}

export function useUveryQuery(mesic: string, enabled = true) {
  return useQuery({
    queryKey: ['leasing', 'prehled', 'uvery', mesic],
    enabled,
    queryFn: ({ signal }) =>
      api.get<{ uvery: PrehledUver[] }>(
        `/v1/leasing/prehled/uvery${buildQuery({ mesic })}`,
        { signal },
      ),
    placeholderData: (previous) => previous,
  });
}

export function useUveryUkonceniQuery(mesic: string, enabled = true) {
  return useQuery({
    queryKey: ['leasing', 'prehled', 'uvery-ukonceni', mesic],
    enabled,
    queryFn: ({ signal }) =>
      api.get<{ uvery: PrehledUverUkonceni[] }>(
        `/v1/leasing/prehled/uvery-ukonceni${buildQuery({ mesic })}`,
        { signal },
      ),
    placeholderData: (previous) => previous,
  });
}

export function useLeasingSearchQuery(filters: SearchFilters) {
  const enabled = Object.values(filters).some((v) => v && v.trim());
  return useQuery({
    queryKey: ['leasing', 'search', filters],
    enabled,
    queryFn: ({ signal }) =>
      api.get<SearchResponse>(
        `/v1/leasing/smlouvy/search${buildQuery({ ...filters })}`,
        { signal },
      ),
    placeholderData: (previous) => previous,
  });
}


// ==================== Contract partial update (dop_text / termination) ====================

/**
 * `PUT /v1/leasing/smlouvy/{id}` — smlouva actions:
 * "Ostatní ujednání" free text and předčasné ukončení (empty string clears
 * the termination date — NULLIF pattern).
 */
export function useUpdateSmlouvaMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { dop_text?: string; predcasne_ukonceno?: string | null }) =>
      api.put<{ status: 'ok' }>(pathFor('smlouva', id), body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leasing'] }),
  });
}

// ==================== Delete nabídka ====================

/** Delete an offer — cascades parametry + naklady. */
export function useDeleteNabidkaMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ status: 'ok' }>(`/v1/leasing/nabidky/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leasing'] }),
  });
}

// ==================== Attachments (přílohy) ====================

export interface LeasingAttachment {
  id_prilohy: number;
  nazev: string;
  poznamka: string;
  klic: string;
}

export function useLeasingAttachmentsQuery(kind: LeasingKind, id: number | null) {
  return useQuery({
    queryKey: ['leasing', 'attachments', kind, id],
    enabled: id != null && Number.isFinite(id),
    queryFn: ({ signal }) =>
      api.get<{ attachments: LeasingAttachment[] }>(
        pathFor(kind, id!, '/attachments'),
        { signal },
      ),
  });
}

export function useUploadLeasingAttachmentMutation(kind: LeasingKind, id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { file: File; nazev?: string; poznamka?: string }) => {
      // Multipart upload — bypasses the JSON `api` wrapper like the vozidla
      // attachment hooks (lib/api forces a JSON content type).
      const fd = new FormData();
      fd.append('file', input.file);
      if (input.nazev) fd.append('nazev', input.nazev);
      if (input.poznamka) fd.append('poznamka', input.poznamka);
      const headers: Record<string, string> = {
        Accept: 'application/json',
        ...getAuthHeaders(),
      };
      const res = await fetch(`/api${pathFor(kind, id, '/attachments')}`, {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
        headers,
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      return (await res.json()) as { id: number };
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['leasing', 'attachments', kind, id] }),
  });
}

export function useUpdateLeasingAttachmentMutation(kind: LeasingKind, id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { fileId: number; nazev: string; poznamka?: string }) =>
      api.put<{ status: 'ok' }>(`${pathFor(kind, id, '/attachments')}/${input.fileId}`, {
        nazev: input.nazev,
        poznamka: input.poznamka ?? '',
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['leasing', 'attachments', kind, id] }),
  });
}

export function useDeleteLeasingAttachmentMutation(kind: LeasingKind, id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fileId: number) =>
      api.delete<{ status: 'ok' }>(`${pathFor(kind, id, '/attachments')}/${fileId}`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['leasing', 'attachments', kind, id] }),
  });
}

// ==================== Evaluation (vyhodnocení smlouvy) ====================

export interface EvaluationCalculated {
  cenaBezDane: number;
  zakladniCena: number;
  splatkaBezDane: number;
  splatka: number;
  zustatkovaCena: number;
  sleva: number;
  cenaposleve: number;
  castkaNaklady: number;
  prijateSplatky: number;
  castkaUveru: number;
  castkaPojistne: number;
  vyhodnoceni: number;
  vyhodnoceniSeZusratkovou: number;
}

export interface EvaluationResponse {
  evaluation: {
    contract: Record<string, unknown>;
    customer: Record<string, unknown> | null;
    company: Record<string, unknown> | null;
    seller: Record<string, unknown> | null;
    loanInsurance: Record<string, unknown> | null;
    costs: Array<Record<string, unknown>>;
    calculated: EvaluationCalculated;
  };
}

/**
 * `GET /v1/leasing/smlouvy/{id}/evaluation`. Read-only: the legacy page's render-time
 * persist wrote into the *vozidla* module's table sharing the same numeric id
 * and is intentionally not replicated here.
 */
export function useEvaluationQuery(id: number | null) {
  return useQuery({
    queryKey: ['leasing', 'smlouva', id, 'evaluation'],
    enabled: id != null && Number.isFinite(id),
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      api.get<EvaluationResponse>(`/v1/leasing/smlouvy/${id}/evaluation`, { signal }),
  });
}
